import { Directive, ElementRef, EventEmitter, HostListener, Input, Output, Renderer2, OnDestroy, OnInit} from '@angular/core';

export interface LassoSelectionEvent {
  added: HTMLElement[];
  removed: HTMLElement[];
  selected: HTMLElement[];
}

@Directive({
  selector: '[appLassoSelect]'
})
export class LassoSelectDirective implements OnInit, OnDestroy {
  @Input() enabled = true;
  @Input() selectableClass = 'thumbnail-selectable';
  @Output() lassoSelect = new EventEmitter<LassoSelectionEvent>();
  
  private isSelecting = false;
  private selectionBox: HTMLElement | null = null;
  private startPoint = { x: 0, y: 0 };
  private endPoint = { x: 0, y: 0 };
  private selectedElements = new Set<HTMLElement>();
  private previousSelection = new Set<HTMLElement>();
  
  constructor(
    private el: ElementRef,
    private renderer: Renderer2
  ) {}

  ngOnInit() {
    if (!this.el?.nativeElement) return;
    
    // Set container to relative positioning for the selection box
    this.renderer.setStyle(this.el.nativeElement, 'position', 'relative');
    this.renderer.setStyle(this.el.nativeElement, 'user-select', 'none');
  }

  ngOnDestroy() {
    this.removeSelectionBox();
  }

  @HostListener('mousedown', ['$event'])
  onMouseDown(event: MouseEvent) {
    if (!this.enabled || event.button !== 0 || !this.el?.nativeElement) return;
    
    // Check if clicking on a selectable item
    const target = event.target as HTMLElement;
    const clickedOnThumbnail = target.closest(`.${this.selectableClass}`);
    
    if (clickedOnThumbnail) {
      // If clicking on a thumbnail with any modifier key, don't start lasso
      if (event.ctrlKey || event.metaKey || event.shiftKey) {
        return; // Let the click bubble up for normal selection
      }
      // If clicking on a thumbnail without modifiers, also don't start lasso, This allows normal single click selection
      return;
    }
    
    // Only start lasso selection if clicking on empty space
    event.preventDefault();
    this.isSelecting = true;
    
    this.startPoint = {
      x: event.pageX,
      y: event.pageY
    };
    
    // Store current selection
    const currentlySelected = this.el.nativeElement.querySelectorAll(`.${this.selectableClass}.selected`);
    this.previousSelection = new Set(Array.from(currentlySelected) as HTMLElement[]);
    
    this.createSelectionBox();
  }

  @HostListener('window:mousemove', ['$event'])
  onMouseMove(event: MouseEvent) {
    if (!this.isSelecting || !this.selectionBox) return;
    
    event.preventDefault();
    
    // Use page coordinates to handle body scroll correctly
    this.endPoint = {
      x: event.pageX,
      y: event.pageY
    };
    
    this.updateSelectionBox();
    this.updateSelection();
  }

  @HostListener('window:mouseup', ['$event'])
  onMouseUp(event: MouseEvent) {
    if (!this.isSelecting) return;
    
    this.isSelecting = false;
    this.removeSelectionBox();
    
    // Emit final selection
    this.emitSelection();
  }

  @HostListener('window:scroll', ['$event'])
  onScroll(event: Event) {
    if (!this.isSelecting || !this.selectionBox) return;
    
    // Update selection box position during scroll
    this.updateSelectionBox();
    this.updateSelection();
  }

  private createSelectionBox() {
    if (!this.el?.nativeElement) return;
    
    this.selectionBox = this.renderer.createElement('div');
    this.renderer.addClass(this.selectionBox, 'lasso-selection-box');
    
    // Use fixed positioning to work with body scroll
    this.renderer.setStyle(this.selectionBox, 'position', 'fixed');
    this.renderer.setStyle(this.selectionBox, 'pointer-events', 'none');
    this.renderer.setStyle(this.selectionBox, 'z-index', '9999');
    // this.renderer.setStyle(this.selectionBox, 'border', '2px dashed var(--RAblue)');
    // this.renderer.setStyle(this.selectionBox, 'background-color', 'rgba(52, 132, 206, 0.1)');
    this.renderer.setStyle(this.selectionBox, 'border-radius', '4px');
    
    // Append to body instead of container for fixed positioning
    this.renderer.appendChild(document.body, this.selectionBox);
  }

  private updateSelectionBox() {
    if (!this.selectionBox) return;
    
    // Calculate position and size
    const left = Math.min(this.startPoint.x, this.endPoint.x);
    const top = Math.min(this.startPoint.y, this.endPoint.y);
    const width = Math.abs(this.endPoint.x - this.startPoint.x);
    const height = Math.abs(this.endPoint.y - this.startPoint.y);

    // Use viewport coordinates since we're using fixed positioning
    this.renderer.setStyle(this.selectionBox, 'left', `${left}px`);
    this.renderer.setStyle(this.selectionBox, 'top', `${top}px`);
    this.renderer.setStyle(this.selectionBox, 'width', `${width}px`);
    this.renderer.setStyle(this.selectionBox, 'height', `${height}px`);
  }

  private updateSelection() {
    if (!this.el?.nativeElement) return;
    
    const selectableElements = this.el.nativeElement.querySelectorAll(`.${this.selectableClass}`);
    const selectionRect = this.getSelectionRect();
    
    const newSelection = new Set<HTMLElement>();
    
    selectableElements.forEach((element: HTMLElement) => {
      // Get element position relative to viewport (handles body scroll)
      const elementRect = element.getBoundingClientRect();
      
      // Convert to page coordinates for comparison
      const elementPageRect = {
        left: elementRect.left + window.pageXOffset,
        top: elementRect.top + window.pageYOffset,
        right: elementRect.right + window.pageXOffset,
        bottom: elementRect.bottom + window.pageYOffset
      };
      
      if (this.isIntersecting(selectionRect, elementPageRect)) {
        newSelection.add(element);
        this.renderer.addClass(element, 'lasso-selecting');
      } else {
        this.renderer.removeClass(element, 'lasso-selecting');
      }
    });
    
    this.selectedElements = newSelection;
  }

  private getSelectionRect() {
    return {
      left: Math.min(this.startPoint.x, this.endPoint.x),
      top: Math.min(this.startPoint.y, this.endPoint.y),
      right: Math.max(this.startPoint.x, this.endPoint.x),
      bottom: Math.max(this.startPoint.y, this.endPoint.y)
    };
  }

  private isIntersecting(rect1: any, rect2: any): boolean {
    // Check if rectangles overlap
    const horizontalOverlap = rect1.left <= rect2.right && rect1.right >= rect2.left;
    const verticalOverlap = rect1.top <= rect2.bottom && rect1.bottom >= rect2.top;
    
    return horizontalOverlap && verticalOverlap;
  }

  private removeSelectionBox() {
    if (this.selectionBox && this.selectionBox.parentNode) {
      // Remove from body since we appended it there
      this.renderer.removeChild(document.body, this.selectionBox);
      this.selectionBox = null;
    }
    
    // Remove selecting class from all elements
    if (this.el?.nativeElement) {
      const selectingElements = this.el.nativeElement.querySelectorAll('.lasso-selecting');
      selectingElements.forEach((el: HTMLElement) => {
        this.renderer.removeClass(el, 'lasso-selecting');
      });
    }
  }

  private emitSelection() {
    // For lasso selection, we want different behavior than click selection, 
    // If dragging over already selected items, replace selection
    // If dragging over new items only, add to selection
    
    const hasOverlapWithPrevious = Array.from(this.selectedElements).some(el => 
      this.previousSelection.has(el)
    );
    
    let finalSelection: HTMLElement[];
    let added: HTMLElement[];
    let removed: HTMLElement[];
    
    if (hasOverlapWithPrevious && this.selectedElements.size > 0) {
      // Lasso includes some already selected items - replace entire selection
      finalSelection = Array.from(this.selectedElements);
      added = finalSelection;
      removed = Array.from(this.previousSelection);
    } else if (this.selectedElements.size > 0) {
      // Lasso only has new items - add to existing selection
      const combined = new Set([...this.previousSelection, ...this.selectedElements]);
      finalSelection = Array.from(combined);
      added = Array.from(this.selectedElements);
      removed = [];
    } else {
      // Empty lasso - keep existing selection
      finalSelection = Array.from(this.previousSelection);
      added = [];
      removed = [];
    }
    
    this.lassoSelect.emit({
      added,
      removed,
      selected: finalSelection
    });
  }

  public clearSelection() {
    this.selectedElements.clear();
    this.previousSelection.clear();
    
    // Remove selecting classes
    if (this.el?.nativeElement) {
      const selectingElements = this.el.nativeElement.querySelectorAll('.lasso-selecting');
      selectingElements.forEach((el: HTMLElement) => {
        this.renderer.removeClass(el, 'lasso-selecting');
      });
    }
    
    this.emitSelection();
  }
}